import React, { useMemo, useState, useEffect } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { HiOutlineMenuAlt3 } from "react-icons/hi";
import { IoClose } from "react-icons/io5";
import { MdDashboard } from "react-icons/md";
import { FaUsers, FaCog, FaUserPlus } from "react-icons/fa";

// Sidebar menu array (bind routes from here)
import { FaBookOpen } from "react-icons/fa";
import { CgProfile, CgUserList } from "react-icons/cg";
import { AiOutlineEye } from "react-icons/ai";
import { IoCreateOutline, IoPricetagOutline } from "react-icons/io5";
import { RiLockPasswordLine } from "react-icons/ri";
import { FiLogOut } from "react-icons/fi";

//import logo from "../assests/logo.jpeg"; // update path
import { FaWebAwesome } from "react-icons/fa6";
import { useAuth } from "../context/AuthContext";
import { HiOutlineDocumentText } from "react-icons/hi";

const navLinkClass = ({ isActive }) =>
    `
    flex items-center gap-3 px-3 py-2.5 rounded-lg
    text-sm font-medium
    transition-all
    focus-visible:outline-none
    focus-visible:ring-2 focus-visible:ring-blue-500/40
    ${isActive
        ? "bg-gradient-to-r from-blue-500/15 to-indigo-500/15 text-blue-600"
        : "text-gray-700 hover:bg-blue-50 hover:text-blue-600"
    }
  `;


export default function DashboardLayout() {
    const [isOpen, setIsOpen] = useState(false);
    const location = useLocation();
    const { logout } = useAuth();
    const { user, login } = useAuth();
    const [userData, setUserData] = useState({
        name: "",
        email: "",
    });

    useEffect(() => {
        if (user) {
            setUserData({
                name: user.name || "",
                email: user.email || ""
            });
        }
    }, [user]);

    const menuItems = useMemo(
        () => [
            // { label: "Dashboard", to: "/dashboard", icon: <MdDashboard size={18} /> },

            { label: "Profile", to: "/dashboard/profile", icon: <CgProfile size={18} /> },
            { label: "View Admin", to: "/dashboard/view-admin", icon: <CgUserList size={18} /> },
            { label: "Create Admin", to: "/dashboard/admin", icon: <FaUserPlus size={16} /> },
            { label: "View Course", to: "/dashboard/view-course", icon: <AiOutlineEye size={18} /> },
            { label: "Create Course", to: "/dashboard/create-course", icon: <IoCreateOutline size={18} /> },
            { label: "Create Plan", to: "/dashboard/create-pricing", icon: <IoPricetagOutline size={18} /> },
            { label: "View Plan", to: "/dashboard/view-plan", icon: <HiOutlineDocumentText size={18} /> },
            // { label: "Change Password", to: "/dashboard/change-password", icon: <RiLockPasswordLine size={18} /> },
            { label: "View Site", to: "/", icon: <FaWebAwesome size={16} /> },
        ],
        []
    );

    // Close mobile sidebar on route change
    useEffect(() => {
        setIsOpen(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [location.pathname]);

    // Close on ESC (Accessibility)
    useEffect(() => {
        const onEsc = (e) => {
            if (e.key === "Escape") setIsOpen(false);
        };
        window.addEventListener("keydown", onEsc);
        return () => window.removeEventListener("keydown", onEsc);
    }, []);

    return (
        <div className="min-h-screen flex flex-col md:flex-row bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">

  {/* Mobile Overlay */}
  {isOpen && (
    <button
      className="fixed inset-0 bg-white z-40 md:hidden"
      aria-label="Close sidebar overlay"
      onClick={() => setIsOpen(false)}
    />
  )}

  {/* Sidebar */}
  <aside
    className={`
      fixed md:static top-0 left-0 z-50
      h-screen w-64 md:w-72
      bg-gradient-to-b from-blue-50 via-indigo-50 to-purple-50
      border-r border-blue-100
      transform transition-transform duration-200 ease-in-out
      ${isOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
    `}
    aria-label="Dashboard Sidebar"
  >
    <div className="h-full flex flex-col">

      {/* Branding Top */}
      <div className="h-16 px-4 flex items-center gap-3 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-indigo-600">
        <img
          src="/images/logo.png"
          alt="Well Spoken Logo"
          className="h-10 w-10 object-contain rounded-md bg-white p-1"
        />
        <div className="leading-tight text-white">
          <p className="font-bold text-sm">Well Spoken</p>
          <p className="text-xs text-white/80">Professor Panel</p>
        </div>
      </div>

      {/* Scrollable Menu */}
      <nav className="flex-1 overflow-y-auto px-3 py-4" aria-label="Sidebar Navigation">
        <div className="flex flex-col gap-2">
          {menuItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md transition-colors
                ${isActive ? 'bg-indigo-100 text-indigo-700 font-semibold' : 'text-gray-700 hover:bg-indigo-50'}`
              }
            >
              <span aria-hidden="true">{item.icon}</span>
              <span>{item.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>

      {/* User Profile Bottom */}
      <div className="border-t border-border p-4 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center font-bold text-white">
            {user.user.name?.charAt(0)?.toUpperCase()}
          </div>
          <div className="min-w-0 flex flex-col gap-1">
            <p className="text-sm font-semibold text-gray-900 truncate">{user.user.name}</p>
            <p className="text-xs text-gray-600 truncate">{user.user.email}</p>
            <button
              type="button"
              onClick={logout}
              aria-label="Log out"
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 rounded-md transition w-fit"
            >
              <FiLogOut className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </aside>

  {/* Main Content */}
  <div className="flex-1 flex flex-col h-screen overflow-hidden">

    {/* Topbar */}
    <header className="h-16  border-b border-gray-200 flex items-center justify-between px-4 sticky top-0 z-30 bg-indigo-600">
      <button
        className="md:hidden inline-flex items-center justify-center rounded-lg p-2 text-white hover:bg-indigo-500 transition focus:outline-none focus-visible:ring-2 "
        onClick={() => setIsOpen(true)}
        aria-label="Open sidebar menu"
      >
        <HiOutlineMenuAlt3 size={22} />
      </button>

      <div className="flex flex-col leading-tight">
        <h1 className="text-sm sm:text-base font-bold text-white">Dashboard</h1>
        <p className="text-xs font-bold text-white hidden sm:block">Manage courses, profile, and settings</p>
      </div>

      <div className="text-xs text-white/70 hidden sm:block">{new Date().toLocaleDateString()}</div>
    </header>

    {/* Page Content */}
    <main className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gradient-to-br from-blue-50/60 via-indigo-50/60 to-purple-50/60">
      <Outlet />
    </main>
  </div>
</div>

    );
}
